import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/align", label: "A.L.I.G.N.™" },
  { to: "/process", label: "Process" },
  { to: "/blog", label: "Blog" },
  { to: "/assessment", label: "Assessment" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--bone)]/85 backdrop-blur-md border-b border-[rgba(10,31,68,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <img
            src="/logo-mark-192.png"
            alt="Freedom In Me"
            className="w-10 h-10 object-contain"
            width={40}
            height={40}
          />
          <div className="leading-tight whitespace-nowrap">
            <div className="font-display text-[17px] tracking-tight text-[var(--navy-900)]">
              Freedom In Me
            </div>
            <div className="text-[9px] tracking-[0.28em] uppercase text-[var(--muted)]">
              Systems · Leadership · A.I.
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="text-[13px] tracking-[0.18em] uppercase font-medium text-[var(--muted)] hover:text-[var(--navy-900)] transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.18em] uppercase font-medium transition-colors ${
                    isActive
                      ? "text-[var(--navy-900)]"
                      : "text-[var(--muted)] hover:text-[var(--navy-900)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href="/assessment"
            data-testid="nav-cta-book-call"
            className="btn-primary px-5 py-2.5 text-[12px] tracking-[0.22em] uppercase font-semibold rounded-full"
          >
            Take The Free Index
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-[var(--navy-900)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        data-testid="mobile-menu"
        className={`md:hidden mobile-menu ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } bg-[var(--bone)] border-b border-[rgba(10,31,68,0.08)]`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                className="text-[14px] tracking-[0.18em] uppercase text-[var(--navy-900)]"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className="text-[14px] tracking-[0.18em] uppercase text-[var(--navy-900)]"
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href="/assessment"
            className="btn-primary px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold rounded-full text-center"
          >
            Take The Free Index
          </a>
        </div>
      </div>
    </header>
  );
}
